'use client'

import React, { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Input } from "../../@/components/ui/input";
import { Form, FormField } from "@/components/ui/form";
import { Textarea } from "../../@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";
import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "../../@/components/ui/field";
import { cn } from "@/lib/utils";

const editRoleSchema = z.object({
  title: z.string().min(1, "This is required"),
  description: z.string().min(1, "This is required"),
});

const EditRoleDialog = ({ role, open, onOpenChange, onUpdated }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getToken } = useAuth();

  const roleId = role?.id;
  const initialTitle = useMemo(() => role?.title ?? role?.role ?? "", [role]);
  const initialDescription = useMemo(
    () => role?.description ?? "",
    [role]
  );

  const form = useForm({
    resolver: zodResolver(editRoleSchema),
    defaultValues: {
      title: "",
      description: "",
    },
  });

  useEffect(() => {
    if (!open) return;

    form.reset({
      title: initialTitle || "",
      description: initialDescription || "",
    });
  }, [open, initialTitle, initialDescription, form]);

  const onSubmit = async (values) => {
    try {
      if (roleId === null || roleId === undefined || roleId === "Not available") {
        toast.error("Missing role id.");
        return;
      }

      setIsSubmitting(true);

      const token = await getToken();
      if (!token) {
        toast.error("Authentication required to edit a role.");
        return;
      }

      const payload = {
        title: values.title,
        description: values.description,
      };

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      await axios.put(`${api_endpoint}/user/roles/edit/${roleId}`, payload, reqConfig);

      toast.success("Role updated successfully!");
      if (onUpdated) onUpdated();
      if (onOpenChange) onOpenChange(false);
    } catch (error) {
      console.error("Failed to update role:", error);
      toast.error("Failed to update role.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-(--neutral-900)">
            Edit Role
          </DialogTitle>
          <DialogDescription className="font-body text-(--neutral-700)">
            Update the role title and description.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field, fieldState }) => (
                <Field>
                  <FieldLabel>Role Title *</FieldLabel>
                  <FieldContent>
                    <Input
                      placeholder="e.g. Instructor"
                      {...field}
                      className={cn(
                        "font-body",
                        fieldState.error &&
                          "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                      )}
                    />
                    <FieldError
                      errors={
                        fieldState.error
                          ? [{ message: fieldState.error.message }]
                          : []
                      }
                    />
                  </FieldContent>
                </Field>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field, fieldState }) => (
                <Field>
                  <FieldLabel>Role Description *</FieldLabel>
                  <FieldContent>
                    <Textarea
                      placeholder="Describe the role..."
                      rows={3}
                      {...field}
                      className={cn(
                        "font-body resize-none",
                        fieldState.error &&
                          "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                      )}
                    />
                    <FieldError
                      errors={
                        fieldState.error
                          ? [{ message: fieldState.error.message }]
                          : []
                      }
                    />
                  </FieldContent>
                </Field>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <span className="flex items-center justify-center w-full">
                    <ClipLoader size={20} color="#fff" />
                  </span>
                ) : (
                  "Save changes"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EditRoleDialog;
